import { HttpErrorResponse, HttpRequest } from '@angular/common/http';

/**
 * URLs that should be skipped from reporting (e.g., polling endpoints)
 */
const SKIP_URLS: ReadonlyArray<string> = [
    'api/notifications',
];

/**
 * Time window for deduplication in milliseconds
 */
export const THROTTLE_WINDOW_MS = 60000;

/**
 * Normalizes an API path by replacing numeric IDs with `:id` placeholders.
 * Also removes query strings.
 *
 * @example
 * normalizeApiPath('/api/domains/35914/pages') // → '/api/domains/:id/pages'
 * normalizeApiPath('/api/domains/123?foo=bar') // → '/api/domains/:id'
 */
export function normalizeApiPath (url: string): string {
    try {
        const urlObj = new URL(url, window.location.origin);
        let pathname = urlObj.pathname;

        // Replace numeric IDs with :id placeholder
        // Match segments that are purely digits, but preserve query strings separately
        pathname = pathname.replace(/\/\d+(?=\/|$)/g, '/:id');

        return pathname;
    } catch {
        // For relative URLs or invalid URLs, try basic string manipulation
        // Remove query string
        const withoutQuery = url.split('?')[0];
        // Replace numeric segments
        return withoutQuery.replace(/\/\d+(?=\/|$)/g, '/:id');
    }
}

/**
 * Determines if a URL should be skipped from reporting.
 */
export function shouldSkipUrl (url: string): boolean {
    return SKIP_URLS.some(skipUrl => url.includes(skipUrl));
}

/**
 * Determines if an HTTP error should be reported to Rollbar.
 *
 * Rules:
 * - Skip: status 0/-1 when offline, aborts, skipped URLs
 * - Report: status 0/-1 (when online), status >= 500
 * - Skip: all 4xx (user-driven errors)
 */
export function shouldReport (
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
): boolean {
    // Skip aborts
    if (error.statusText === 'abort') {
        return false;
    }

    // Skip when offline (user's network, not our bug)
    if (!navigator.onLine) {
        return false;
    }

    // Skip noisy/polling URLs
    if (shouldSkipUrl(request.url)) {
        return false;
    }

    // Report status 0/-1 (CORS, blocked preflight, proxy/VPN drop, timeouts)
    if (error.status === 0 || error.status === -1) {
        return true;
    }

    // Report server errors (5xx)
    if (error.status >= 500) {
        return true;
    }

    // Skip all other errors (4xx are user-driven)
    return false;
}

/**
 * Type for the report data payload
 * Uses index signature to be compatible with Record<string, unknown>
 */
export interface HttpFailureReportData {
    [key: string]: unknown;
    status: number;
    statusText: string;
    method: string;
    path: string;
    host: string;
    origin: string;
    isMicrofrontend: boolean;
    onLine: boolean;
    serviceWorkerActive: boolean;
    durationMs: number;
    backendMessage?: string;
    suppressedCount?: number;
}

/**
 * Report structure returned by buildReport
 */
export interface HttpFailureReport {
    message: string;
    level: 'warning' | 'error' | 'critical';
    data: HttpFailureReportData;
}

/**
 * Truncates a string to a maximum length.
 */
function truncate (str: string, maxLength: number): string {
    if (str.length <= maxLength) {
        return str;
    }
    return str.substring(0, maxLength) + '...';
}

/**
 * Extracts a backend message from the error response, if present.
 */
function extractBackendMessage (error: HttpErrorResponse): string | undefined {
    const errorBody = error.error;
    if (errorBody && typeof errorBody === 'object') {
        const message = (errorBody as { message?: string }).message;
        if (typeof message === 'string') {
            return truncate(message, 500);
        }
    }
    return undefined;
}

/**
 * Resolves the host the request was sent to (the API host), not the page host.
 */
function getRequestHost (url: string): string {
    try {
        return new URL(url, window.location.origin).host;
    } catch {
        return window.location.host;
    }
}

/**
 * Builds the report data for an HTTP failure.
 */
export function buildReport (
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    durationMs: number,
    suppressedCount = 0,
    isMicrofrontend = false,
): HttpFailureReport {
    const normalizedPath = normalizeApiPath(request.url);
    const method = request.method;
    const status = error.status;

    // Determine level based on status
    let level: 'warning' | 'error' | 'critical' = 'error';
    if (status === 502) {
        level = 'critical';
    } else if (status === 0 || status === -1) {
        level = 'warning';
    }

    // Build message: stable, groups well in Rollbar
    // HTTP <METHOD> <normalized path> failed (<status>)
    const message = `HTTP ${method} ${normalizedPath} failed (${status})`;

    const data: HttpFailureReportData = {
        status,
        statusText: error.statusText,
        method,
        path: normalizedPath,
        host: getRequestHost(request.url),
        origin: window.location.origin,
        isMicrofrontend,
        onLine: navigator.onLine,
        serviceWorkerActive: !!navigator.serviceWorker?.controller,
        durationMs,
    };

    // Add optional fields only if present
    const backendMessage = extractBackendMessage(error);
    if (backendMessage) {
        data.backendMessage = backendMessage;
    }

    if (suppressedCount > 0) {
        data.suppressedCount = suppressedCount;
    }

    return { message, level, data };
}

/**
 * Generates a throttle key for deduplication.
 */
export function getThrottleKey (
    method: string,
    normalizedPath: string,
    status: number,
): string {
    return `${method}:${normalizedPath}:${status}`;
}
